import {
  Box,
  Center,
  Stack,
  Text,
  Heading,
} from "@chakra-ui/layout";
import {
  Switch,
  useColorModeValue as mode,
  Tooltip,
} from "@chakra-ui/react";
import SearchBar from "components/search-bar";
import Tweets from "components/tweets";
import { useEffect, useState, useContext, useRef } from "react";

import { useSession } from "next-auth/react";

import ProtectedRoute from "components/protected-route";
import SubscribePopup from "components/subscribe-popup";
import { Page } from "components/page";
import { useMiscellaneousContext } from "context/miscellaneousContext";
import { getList } from "../controllers/bookmark";
import { search } from "../controllers/search";
import PuffLoader from "react-spinners/PuffLoader";
import { useFirebaseUser } from "../utils/useFirebaseUser";
import { useOnboardingSteps } from "components/onboard-panel/useOnboardingSteps";
import { getAccount } from "../utils/sessionHelper";
import { setInLocalStorage } from "../utils/helpers";
import { MainContext } from "../context/mainContext";
import { useStats } from "hooks/useStats";
import { layerStyle, textStyle, variant } from "theme/names";
import { TweetContext } from "context/tweetContext";
import { getCachedKey, getCachedKeyTime, updateCachedKey, updateCachedKeyTime } from "../utils/localforageHelpers";

const trendingTopics = [
  "marketing",
  "crypto",
  "saas",
  "gaming",
  "startup",
  "audience",
  "coding",
];

const defaultFiltersValues = {
  defaultMinDaysOld: 30,
  defaultMaxDaysOld: 1000,
  defaultMinLikes: 40,
  defaultMinRt: 0,
  defaultHashtag: false,
  defaultDollar: false,
  defaultNoCrypto: true,
  defaultNoThread: true,
  defaultOnlyThread: false,
  defaultIncludeTwitter: true,
  defaultIncludeLinkedin: true,
  defaultOnlyCarousel: 0,
}

const FILTER_KEY = "searchTopic_search_filters";

export const Application = () => {
  const { defaultMinDaysOld,
    defaultMaxDaysOld,
    defaultMinLikes,
    defaultMinRt,
    defaultHashtag,
    defaultDollar,
    defaultNoCrypto,
    defaultNoThread,
    defaultOnlyCarousel,
    defaultOnlyThread,
    defaultIncludeTwitter,
    defaultIncludeLinkedin,
  } = defaultFiltersValues;
  const { data: session, status } = useSession() ?? {}
  const loading = status === "loading"

  const [isPersonalizedSorting, setIsPersonalizedSorting] = useState(true);
  const [savedTweet, setSavedTweet] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [data, setData] = useState<any>(null);
  const [suggestions, setSuggestions] = useState<string[]>(trendingTopics);
  const [lastTopic, setLastTopic] = useState<string>("");

  const [nbMinDaysOld, setNbMinDaysOld] = useState(defaultMinDaysOld);
  const [nbMaxDaysOld, setNbMaxDaysOld] = useState(defaultMaxDaysOld);
  const [nbMinLikes, setNbMinLikes] = useState(defaultMinLikes);
  const [nbMinRt, setNbMinRt] = useState(defaultMinRt);
  const [noHashtag, setNoHashtag] = useState<boolean>(defaultHashtag);
  const [noDollar, setNoDollar] = useState<boolean>(defaultDollar);
  const [noCrypto, setNoCrypto] = useState<boolean>(defaultNoCrypto);
  const [noThread, setNoThread] = useState<boolean>(defaultNoThread);
  const [onlyThread, setOnlyThread] = useState<boolean>(defaultOnlyThread);
  const [includeTwitter, setIncludeTwitter] = useState<boolean>(defaultIncludeTwitter);
  const [includeLinkedin, setIncludeLinkedin] = useState<boolean>(defaultIncludeLinkedin);
  const [includeCarrousel, setIncludeCarrousel] = useState<number>(defaultOnlyCarousel);
  // const [noCarrousel, setNoCarrousel] = useState<boolean>(defaultNoCarrousel);
  const [appliedFilters, setAppliedFilters] = useState<string[]>([]);
  const [isRemovingFilters, setIsRemovingFilters] = useState<boolean>(false);
  const mainContext = useContext(MainContext);

  const [refresh, refresher] = useState({});
  const isAuthenticated = useFirebaseUser();
  const { updateOnboardSteps } = useOnboardingSteps();
  const { updateStatsCounter } = useStats();

  const [cachedPostsLoaded, setCachedPostsLoaded] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const miscellaneousContext: any = useMiscellaneousContext();
  const clearCache = useRef(false);

  const getSavedTweets = async () => {
    if (isAuthenticated) {
      const savedTweets = await getList(getAccount(session).id);
      setSavedTweet(savedTweets);
    }
  };

  useEffect(() => {
    let savedIsPersonalizedSorting = localStorage.getItem('isPersonalizedSorting');
    if (savedIsPersonalizedSorting !== undefined && savedIsPersonalizedSorting !== null)
      setIsPersonalizedSorting(savedIsPersonalizedSorting == "true");
  }, []);

  useEffect(() => {
    getSavedTweets();
  }, [refresh, session, isAuthenticated]);

  // prefill saved filters from localstorage
  useEffect(() => {
    const savedFilters = localStorage.getItem(FILTER_KEY)
    if (savedFilters) {
      const filters = JSON.parse(savedFilters);
      const {
        minDaysOld,
        maxDaysOld,
        minLikes,
        minRt,
        hashtag,
        noDollar,
        noCrypto,
        noThread,
        onlyThread,
        includeTwitter,
        includeLinkedin
      } = filters;

      setNbMinDaysOld(minDaysOld !== undefined ? minDaysOld : defaultMinDaysOld);
      setNbMaxDaysOld(maxDaysOld !== undefined ? maxDaysOld : defaultMaxDaysOld);
      setNbMinLikes(minLikes !== undefined ? minLikes : defaultMinLikes);
      setNbMinRt(minRt !== undefined ? minRt : defaultMinRt);
      setNoHashtag(hashtag !== undefined ? hashtag : defaultHashtag);
      setNoDollar(noDollar !== undefined ? noDollar : defaultDollar);
      setNoCrypto(noCrypto !== undefined ? noCrypto : defaultNoCrypto);
      setNoThread(noThread !== undefined ? noThread : defaultNoThread);
      setOnlyThread(onlyThread !== undefined ? onlyThread : defaultOnlyThread);
      setIncludeTwitter(includeTwitter !== undefined ? includeTwitter : defaultIncludeTwitter);
      setIncludeLinkedin(includeLinkedin !== undefined ? includeLinkedin : defaultIncludeLinkedin);
      setIncludeCarrousel(includeCarrousel !== undefined ? includeCarrousel : defaultOnlyCarousel);
    }
  }, [])

  // track filter changes
  useEffect(() => {
    let changedFilters: { id: string, name: string }[] = [];
    if (nbMinRt !== defaultMinRt) changedFilters.push({ id: "nbMinRt", name: `min ${nbMinRt} RTs` });
    if (nbMinLikes !== defaultMinLikes) changedFilters.push({ id: "nbMinLikes", name: `min ${nbMinLikes} ${nbMinLikes > 0 ? "likes" : "like"}` });
    if (nbMinDaysOld !== defaultMinDaysOld) changedFilters.push({ id: "nbMinDaysOld", name: `min ${nbMinDaysOld} ${nbMinDaysOld > 0 ? "days" : "day"} old` });
    if (nbMaxDaysOld !== defaultMaxDaysOld) changedFilters.push({ id: "nbMaxDaysOld", name: `max ${nbMaxDaysOld} ${nbMaxDaysOld > 0 ? "days" : "day"} old` });
    if (noHashtag !== defaultHashtag) changedFilters.push({ id: "noHashtag", name: "no hashtag" });
    if (noDollar !== defaultDollar) changedFilters.push({ id: "noDollar", name: "no ticker" });
    if (noCrypto !== defaultNoCrypto) changedFilters.push({ id: "noCrypto", name: "no crypto" });
    if (noThread !== defaultNoThread) changedFilters.push({ id: "noThread", name: "no thread" });
    if (onlyThread !== defaultOnlyThread) changedFilters.push({ id: "onlyThread", name: "only thread" });
    if (includeTwitter !== defaultIncludeTwitter) changedFilters.push({ id: "includeTwitter", name: "No tweets" });
    if (includeLinkedin !== defaultIncludeLinkedin) changedFilters.push({ id: "includeLinkedin", name: "No Linkedin posts" });
    if (includeCarrousel !== defaultOnlyCarousel) {
      const filterLabel = () => {
        switch (includeCarrousel) {
          case 1:
            return "Only carrousel";
          case -1:
            return "Exclude carrousel";
          default:
            return "All Types";
        }
      }
      changedFilters.push({ id: "includeCarrousel", name: filterLabel() });
    }
    // @ts-ignore
    setAppliedFilters(changedFilters)
  }, [
    nbMinRt,
    nbMinLikes,
    nbMinDaysOld,
    nbMaxDaysOld,
    noHashtag,
    noDollar,
    noThread,
    onlyThread,
    includeTwitter,
    includeLinkedin,
    includeCarrousel
  ])

  const handleRemoveFilter = (id: string) => {
    setIsRemovingFilters(true)
    switch (id) {
      case "nbMinRt":
        setNbMinRt(defaultMinRt);
        break;
      case "nbMinLikes":
        setNbMinLikes(defaultMinLikes);
        break;
      case "nbMinDaysOld":
        setNbMinDaysOld(defaultMinDaysOld);
        break;
      case "nbMaxDaysOld":
        setNbMaxDaysOld(defaultMaxDaysOld);
        break;
      case "noHashtag":
        setNoHashtag(defaultHashtag);
        break;
      case "noDollar":
        setNoDollar(defaultDollar);
        break;
      case "noCrypto":
        setNoCrypto(defaultNoCrypto);
        break;
      case "noThread":
        setNoThread(defaultNoThread);
        break;
      case "onlyThread":
        setOnlyThread(defaultOnlyThread);
        break;
      case "includeLinkedin":
        setIncludeLinkedin(defaultIncludeLinkedin);
        break;
      case "includeTwitter":
        setIncludeTwitter(defaultIncludeTwitter);
        break;
      case "includeCarrousel":
        setIncludeCarrousel(defaultOnlyCarousel);
    }
  }

  useEffect(() => {
    if (isRemovingFilters) {
      sendRequest(session, lastTopic);
      setIsRemovingFilters(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    const checkCacheAndLoad = async () => {
      const cachedTopic = await getCachedKey("cachedSearchTopic");

      if (typeof cachedTopic === "string" && Boolean(cachedTopic) === true && Boolean(data?.tweets) === false && cachedPostsLoaded === false && session && isAuthenticated && !hasFetched) {
        setLastTopic(cachedTopic);
        sendRequest(session, cachedTopic);
      }
    }
    checkCacheAndLoad();
  }, [cachedPostsLoaded, session, data, isAuthenticated, lastTopic, hasFetched]);

  const sendRequest = async (session, topic) => {

    if (isSending && !clearCache.current) return;
    setIsSending(true);
    miscellaneousContext.setIsDataRefreshing(true);
    let cachedTopic: any = await getCachedKey("cachedSearchTopic");
    let cachedPosts: any = await getCachedKey("cachedSearchPosts");
    let cachedSuggestions: any = await getCachedKey("cachedSearchSuggestions");

    let cachedPostsTime = await getCachedKeyTime("cachedSearchPostsTime");
    const aDayFromNow = new Date().getTime() + 24 * 60 * 60 * 1000;

    if (Boolean(cachedPostsTime) === false) {
      await updateCachedKeyTime("cachedSearchPostsTime", aDayFromNow);
    }

    if (
      new Date().getTime() <= new Date(cachedPostsTime).getTime() &&
      !clearCache.current
    ) {
      if (Boolean(cachedPosts) === true && cachedPosts?.tweets?.length > 0 && Boolean(cachedSuggestions) === true && cachedSuggestions?.length > 0 && Boolean(cachedTopic) === true && topic === cachedTopic) {
        console.log("cache hit");
        setCachedPostsLoaded(true);
        setData(cachedPosts);
        setSuggestions(cachedSuggestions);
        setLastTopic(cachedTopic);
        setIsSending(false);
        setHasFetched(true);
        miscellaneousContext.setIsDataRefreshing(false);
        return;
      } else {
        console.log("cache miss");
        setCachedPostsLoaded(false);
      }
    }
   
    if (!cachedPostsLoaded || clearCache.current || topic !== cachedTopic) {
      console.log("caching...");
      let params = {
        isPersonalizedSorting: isPersonalizedSorting,
        nbMinDaysOld: nbMinDaysOld,
        nbMaxDaysOld: nbMaxDaysOld,
        nbMinLikes: nbMinLikes,
        nbMinRt: nbMinRt,
        noHashtag: noHashtag,
        noDollar: noDollar,
        noCrypto: noCrypto,
        noThread: noThread,
        onlyThread: onlyThread,
        disableTwitter: !includeTwitter,
        disableLinkedin: !includeLinkedin,
        includeCarrousel: includeCarrousel,
      }

      //@ts-ignore
      let data = await search(session, topic, params, undefined, mainContext);

      updateStatsCounter("search_via_search_page");

      // save filters to localstorage
      // @ts-ignore
      const filters = {
        minDaysOld: nbMinDaysOld,
        maxDaysOld: nbMaxDaysOld,
        minLikes: nbMinLikes,
        minRt: nbMinRt,
        hashtag: noHashtag,
        noDollar: noDollar,
        noCrypto: noCrypto,
        noThread: noThread,
        onlyThread: onlyThread,
        includeTwitter: includeTwitter,
        includeLinkedin: includeLinkedin,
        includeCarrousel: includeCarrousel,
      }
      setInLocalStorage(FILTER_KEY, JSON.stringify(filters))

      if (!data || data.success === 0) {
        setData(null);
        await updateCachedKey("cachedSearchPosts", null);
      }
      else {
        setData(data);
        await updateCachedKey("cachedSearchPosts", data);
        if (data?.suggestions?.length) {
          setSuggestions(data.suggestions);
          await updateCachedKey("cachedSearchSuggestions", data.suggestions);
        }
      }
      console.log("setting lastTopic", topic);
      setLastTopic(topic);
      await updateCachedKey("cachedSearchTopic", topic);
      updateOnboardSteps("searchInspiration", "aiWriting");
      await updateCachedKeyTime("cachedSearchPostsTime", aDayFromNow);
      setIsSending(false);
      setHasFetched(true);
      miscellaneousContext.setIsDataRefreshing(false);
    }
  };

  const refreshClickedCallback = async () => {
    setIsSending(false);
    clearCache.current = true;
    await refreshRequest();
  };

  const refreshRequest = async () => {
    sendRequest(session, lastTopic);
  }


  if (!(loading || session)) {
    return <ProtectedRoute />;
  }

  return (
    <Page
      refresher={refresher}
      isLoadingFeature={isSending}
      showLoadingBar={true}
      mainWrapperProps={{
        py: [12, 8],
        maxW: 1500
      }}
      {...{
        title: "Search for any topic and find viral posts to get inspired by",
        description: "We use AI to analyse posts and match them with your search",
        titleRowWrapperProps: { justifyContent: "center" },
        descriptionWrapperProps: { textAlign: "center", pt: 2 }
      }}
    >
      <Stack mt={0}>
        <Center
        >
          <SearchBar
            sendRequest={sendRequest}
            isLoading={isSending}
            // placeholder="Search any topic or @handle"
            placeholder='keyword "exact match" -exclude'
            isSending={isSending}
            setIsSending={setIsSending}
            setData={setData}
            nbMinRt={nbMinRt}
            setNbMinRt={setNbMinRt}
            nbMinLikes={nbMinLikes}
            setNbMinLikes={setNbMinLikes}
            nbMaxDaysOld={nbMaxDaysOld}
            setNbMaxDaysOld={setNbMaxDaysOld}
            nbMinDaysOld={nbMinDaysOld}
            setNbMinDaysOld={setNbMinDaysOld}
            noHashtag={noHashtag}
            setNoHashtag={setNoHashtag}
            noDollar={noDollar}
            setNoDollar={setNoDollar}
            noCrypto={noCrypto}
            setNoCrypto={setNoCrypto}
            noThread={noThread}
            setNoThread={setNoThread}
            onlyThread={onlyThread}
            setOnlyThread={setOnlyThread}
            includeTwitter={includeTwitter}
            setIncludeTwitter={setIncludeTwitter}
            includeLinkedin={includeLinkedin}
            setIncludeLinkedin={setIncludeLinkedin}
            includeCarrousel={includeCarrousel}
            setIncludeCarrousel={setIncludeCarrousel}
            hasCustomFilters={appliedFilters.length > 0}
            suggestions={suggestions}
            align="center"
            withHandleSuggestions={false}
          />
        </Center>
        {
          // session?.user?.data?.isAdmin && ()
          <Center display="flex" flexDirection="column" >
            <Tooltip hasArrow label="We automatically analyse your account to bring you more relevant post suggestions. Click here to modify these settings.">
              <Box display="flex" alignItems="center" mb={3} layerStyle={layerStyle["bg.border.rounded"]} px={3} py={2}>
                <Switch
                  isChecked={isPersonalizedSorting}
                  // mt={4} mb={4} ml={4} 
                  mr={3}
                  size="sm"
                  onChange={() => {
                    setInLocalStorage('isPersonalizedSorting', `${(!isPersonalizedSorting)}`);
                    setIsPersonalizedSorting(!isPersonalizedSorting);
                  }}
                />
                <Text textStyle={textStyle["body.medium.light"]} as="a" href="/settings" >
                  Optimize results for my account
                </Text>
              </Box>
            </Tooltip>
          </Center>
        }
        {
          isSending && !clearCache.current && !hasFetched && !data?.tweets?.length && (
            <Center pt={10}>
              <PuffLoader color={"#0A66C2"} loading={isSending} cssOverride={{ display: "block", margin: "0 auto", borderColor: "red" }} size={50} />
            </Center>
          )
        }
        {
          //@ts-ignore
          (!isSending || clearCache.current || hasFetched) && (data?.tweets?.length) && (
            <Box>
              <Tweets
                //@ts-ignore
                sendRequest={refreshRequest}
                savedTweet={savedTweet}
                //@ts-ignore
                tweets={data.tweets}
                //@ts-ignore
                data={data}
                //@ts-ignore
                setData={setData}
                // @ts-ignore
                filters={appliedFilters}
                // @ts-ignore
                handleRemoveFilter={handleRemoveFilter}
                disableBlur={false}
                refresher={refresher}
                refresh={refresh}
                disableSaveCsv={true}
                disableRefresh={false}
                disableEmbed={true}
                disableTryAgain={false}
                disableBookmarkButton={false}
                disableDescriptionWarning={false}
                disableFollowersCount={false}
                disableIntro={false}
                refreshClickedCallback={refreshClickedCallback}
                source="ez4cast"
              />
            </Box>
          )
        }
        {!loading &&
          //@ts-ignore
          data?.tweets &&
          //@ts-ignore
          !session?.user.data?.subscription?.isSubscribed
          && (!session?.user.data?.remainingFreeSearch || session.user.data.remainingFreeSearch <= 0) && (
            <SubscribePopup />
          )}
      </Stack>
      {/* </Box> */}
    </Page>
  );
};

export default Application;
